import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiRequest } from "@/lib/queryClient";
import { currentLanguage } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Bot, Send, Bookmark, Maximize2, PanelRightOpen, AlertTriangle, RotateCcw, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { StartupPayload } from "@/types/startup";
import { formatRecordDate } from "./records/records-utils";

/** Prose typography classes for AI message markdown rendering */
const AI_MESSAGE_PROSE_CLASSES = `text-sm prose prose-sm prose-gray max-w-none
  prose-p:mb-2 prose-p:last:mb-0
  prose-ul:list-disc prose-ul:pl-4 prose-ul:mb-2
  prose-ol:list-decimal prose-ol:pl-4 prose-ol:mb-2
  prose-li:mb-1
  prose-strong:font-semibold
  prose-code:bg-gray-200 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:before:content-[''] prose-code:after:content-['']
  prose-pre:bg-gray-800 prose-pre:text-white prose-pre:p-2 prose-pre:rounded-md prose-pre:overflow-x-auto prose-pre:mb-2
  prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-3 prose-blockquote:italic prose-blockquote:my-2
  prose-h1:text-lg prose-h1:font-bold prose-h1:mb-2 prose-h1:mt-3
  prose-h2:text-base prose-h2:font-bold prose-h2:mb-2 prose-h2:mt-3
  prose-h3:text-sm prose-h3:font-bold prose-h3:mb-1 prose-h3:mt-2
  prose-hr:my-2 prose-hr:border-gray-300
  prose-a:text-blue-600 prose-a:hover:underline prose-a:no-underline
  prose-table:border-collapse prose-table:w-full prose-table:mb-2
  prose-th:border prose-th:border-gray-300 prose-th:px-2 prose-th:py-1 prose-th:bg-gray-100 prose-th:font-semibold prose-th:text-left
  prose-td:border prose-td:border-gray-300 prose-td:px-2 prose-td:py-1`;

interface ChatInterfaceProps {
  sessionId: string;
  patientId: string;
  patientName: string;
  patientData?: Record<string, unknown>; // Full patient data (vitals, conditions, medications, labs)
  suggestedQuestions?: string[];
  /** Startup generation result; when present it replaces the greeting and suggested questions with the Patient Summary card. */
  startup?: Pick<StartupPayload, 'summary' | 'whatsNew' | 'generatedAt' | 'whatsNewWindow'>;
  /** True while the startup summary is still being generated; shows the new layout with a loading card. */
  startupLoading?: boolean;
  showRightPanelToggle?: boolean;
  rightPanelContent?: 'records' | 'questions';
  onRightPanelToggle?: (content: 'records' | 'questions') => void;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  isVisible?: boolean; // Control visibility without unmounting
  className?: string; // Additional styling for different contexts
}

export interface ChatInterfaceRef {
  askQuestion: (question: string, displayText?: string) => void;
  clearChat: () => void;
}

const ChatInterface = forwardRef<ChatInterfaceRef, ChatInterfaceProps>(
  ({ sessionId, patientName, patientData, suggestedQuestions = [], startup, startupLoading = false, isExpanded = false, onToggleExpand, onRightPanelToggle, isVisible = true, className = '' }, ref) => {
    const { t, i18n } = useTranslation();
    const [inputMessage, setInputMessage] = useState("");
    const [hasInteracted, setHasInteracted] = useState(false);
    const [streamingMessage, setStreamingMessage] = useState("");
    const [isStreaming, setIsStreaming] = useState(false);
    const [pendingUserMessage, setPendingUserMessage] = useState("");
    const [_pendingUserDisplayText, setPendingUserDisplayText] = useState("");
    const [bookmarkedMessages, setBookmarkedMessages] = useState<Set<number>>(new Set());
    const [chatError, setChatError] = useState<{ message: string; failedQuestion: string; failedDisplayText?: string } | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const loadingIndicatorRef = useRef<HTMLDivElement>(null);
    const queryClient = useQueryClient();
    const { toast} = useToast();

    // Extract first name from full patient name
    const firstName = patientName.split(' ')[0];

    const { data: messages = [] } = useQuery({
      queryKey: ["/api/chat-messages/current"],
      queryFn: async () => {
        const response = await fetch("/api/chat-messages/current", {
          credentials: 'include'
        });
        if (!response.ok) throw new Error('Failed to fetch messages');
        const data = await response.json();
        return data.messages || [];
      },
      retry: false, // Don't retry if DB table is missing
      refetchOnWindowFocus: false,
    });

    // Streaming chat message function
    const sendStreamingMessage = async (message: string, displayText?: string) => {
      setChatError(null);
      // Show user's message immediately (use displayText for UI if provided)
      setPendingUserMessage(displayText || message);
      setPendingUserDisplayText(displayText || "");
      setIsStreaming(true);
      setStreamingMessage("");
      setChatError(null);

      // Accumulate the full response
      let fullResponse = "";

      try {
        const response = await fetch("/api/chat/stream", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sessionId,
            message,  // Send full question to AI
            displayMessage: displayText || message,  // Send display text for database
            patientContext: patientData, // Send full patient data to AI
            language: currentLanguage(), // 'en' | 'es' — drives the AI response language
          }),
        });

        if (!response.ok) {
          throw new Error(t('chat.sendFailed'));
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error(t('chat.noResponseStream'));
        }

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = JSON.parse(line.substring(6));

              if (data.error) {
                throw new Error(data.error);
              }

              if (data.content) {
                fullResponse += data.content;
                setStreamingMessage(fullResponse);
              }

              if (data.done) {
                // Streaming complete
                setIsStreaming(false);
                setInputMessage("");

                // Try to load persisted messages from DB.
                // Only clear streaming state if DB messages loaded successfully.
                queryClient.invalidateQueries({ queryKey: ["/api/chat-messages/current"] }).then(() => {
                  return queryClient.refetchQueries({ queryKey: ["/api/chat-messages/current"] });
                }).then(() => {
                  const currentMessages = queryClient.getQueryData<any[]>(["/api/chat-messages/current"]);
                  if (currentMessages && currentMessages.length > 0) {
                    // DB has the messages — safe to clear streaming overlay
                    setStreamingMessage("");
                    setPendingUserMessage("");
                    setPendingUserDisplayText("");
                  }
                  // Otherwise keep streamed content visible as fallback
                }).catch(() => {
                  // DB unavailable — keep streamed content visible
                });
                queryClient.invalidateQueries({ queryKey: ["/api/chat-messages"] });
              }
            }
          }
        }
      } catch (error) {
        setIsStreaming(false);
        setStreamingMessage("");
        // Keep pendingUserMessage visible so user can see their question
        const errorMsg = error instanceof Error ? error.message : t('chat.sendFailedRetry');
        setChatError({
          message: errorMsg,
          failedQuestion: message,
          failedDisplayText: displayText,
        });
      }
    };

    // Expose askQuestion method via ref
    useImperativeHandle(ref, () => ({
      askQuestion: (question: string, displayText?: string) => {
        // Prevent sending if already streaming
        if (isStreaming) {
          console.log('Already streaming, ignoring duplicate question');
          return;
        }
        setInputMessage(displayText || question);
        // Mark that user has interacted with chat
        setHasInteracted(true);
        // Auto-send the question
        setTimeout(() => {
          sendStreamingMessage(question, displayText);
        }, 100);
      },
      clearChat: () => {
        setInputMessage("");
        setStreamingMessage("");
        setIsStreaming(false);
        setPendingUserMessage("");
        setPendingUserDisplayText("");
        setHasInteracted(false);
        // Invalidate the current conversation messages query
        queryClient.invalidateQueries({ queryKey: ["/api/chat-messages/current"] });
      },
    }));

  const scrollToLatestMessage = () => {
    if (messagesContainerRef.current) {
      const messageElements = messagesContainerRef.current.querySelectorAll('[data-message]');
      if (messageElements.length >= 2) {
        // Scroll to show the user's question at the top with answer below
        const userQuestionElement = messageElements[messageElements.length - 2];
        userQuestionElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      } else if (messageElements.length === 1) {
        // If only one message, scroll to it at the top
        const lastElement = messageElements[0];
        lastElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      }
    }
  };

  useEffect(() => {
    // Only scroll when new messages are added (not on initial load)
    if (messages.length > 0) {
      // Small delay to ensure DOM has updated
      setTimeout(() => scrollToLatestMessage(), 150);
    }
  }, [messages.length]);

  // Scroll to show question when pending message appears or streaming starts
  useEffect(() => {
    if (pendingUserMessage || isStreaming) {
      // Scroll to show the question at the top
      setTimeout(() => {
        const messageElements = messagesContainerRef.current?.querySelectorAll('[data-message]');
        if (messageElements && messageElements.length > 0) {
          // Scroll to the last message (user's question)
          const lastMessage = messageElements[messageElements.length - 1];
          lastMessage.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }
      }, 100);
    }
  }, [pendingUserMessage, isStreaming]);

  // If there are existing messages, mark as interacted
  useEffect(() => {
    if (messages.length > 0) {
      setHasInteracted(true);
    }
  }, [messages.length]);

  const handleSendMessage = async () => {
    const message = inputMessage.trim();
    if (!message || isStreaming) return;

    // Mark that user has interacted with chat
    setHasInteracted(true);

    await sendStreamingMessage(message);
  };

  // Send a suggested question or Patient Summary topic as a chat message directly.
  // (Setting the input then calling handleSendMessage on a timer read a stale, empty input.)
  const askStartupTopic = (question: string) => {
    if (isStreaming) return;
    setHasInteracted(true);
    setInputMessage(question);
    sendStreamingMessage(question);
  };

  const showStartupCard = (!!startup || startupLoading) && !hasInteracted;
  const startupUpdatedAt = (() => {
    if (!startup) return null;
    const generated = new Date(startup.generatedAt);
    if (Number.isNaN(generated.getTime())) return null;
    return new Intl.DateTimeFormat(i18n.resolvedLanguage, { timeStyle: 'short' }).format(generated);
  })();

  const renderStartupLoadingCard = () => (
    <div className="rounded-lg border bg-gray-50 px-4 py-6 flex items-center justify-center gap-3" data-testid="startup-loading-card" role="status" aria-live="polite">
      <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
      <span className="text-sm text-text-secondary">{t('chat.startupLoading', { name: firstName })}</span>
    </div>
  );

  const renderStartupCard = () => (startup ? (
    <div className="rounded-lg border bg-gray-50 px-4 pt-5 pb-2" data-testid="startup-summary-card">
      <p className="text-base font-semibold text-text-primary">
        {t('chat.patientSummary')}
        {startupUpdatedAt && (
          <span className="text-sm font-normal text-text-secondary whitespace-nowrap">
            {' '}· {t('chat.updatedAt', { time: startupUpdatedAt })}
          </span>
        )}
      </p>
      <p className="text-base leading-relaxed text-text-secondary mt-1">{startup.summary}</p>
      {startup.whatsNew.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-200">
          <p className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-1">{t('chat.tapTopic')}</p>
          <p className="text-xs text-text-secondary mb-1">
            {startup.whatsNewWindow?.kind === 'sinceVisit'
              ? t('records.whatsNewSinceVisit', { date: formatRecordDate(startup.whatsNewWindow.since, i18n.resolvedLanguage || 'en') })
              : t('records.whatsNewLast24h')}
          </p>
          <div className="-mx-1.5">
            {startup.whatsNew.map((item, index) => (
              <button
                key={index}
                className="w-full flex items-center gap-2 py-2 px-1.5 text-left rounded-md hover:bg-gray-100 transition-colors"
                onClick={() => askStartupTopic(item.ask)}
                disabled={isStreaming}
                data-testid={`button-startup-topic-${index}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                <span className="text-base text-text-primary flex-1 truncate">{item.text}</span>
                <span className="text-sm text-primary flex-shrink-0 font-medium">{t('chat.ask')}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  ) : startupLoading ? renderStartupLoadingCard() : null);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const bookmarkMutation = useMutation({
    mutationFn: async (data: { question: string; aiContext: string; chatMessageId: number }) => {
      // Lengths and ids only: the question text is PHI.
      console.log('[Bookmark] Saving question:', {
        questionLength: data.question.length,
        aiContextLength: data.aiContext.length,
        chatMessageId: data.chatMessageId
      });
      const response = await apiRequest("POST", "/api/saved-questions", {
        question: data.question,
        aiContext: data.aiContext,
        source: 'chat',
        priority: 'normal',
      });
      console.log('[Bookmark] Save response:', response.status);
      return { response, chatMessageId: data.chatMessageId };
    },
    onSuccess: (data) => {
      console.log('[Bookmark] Successfully saved, invalidating queries');
      // Invalidate saved questions cache to refresh the Questions section
      queryClient.invalidateQueries({ queryKey: [`/api/saved-questions/${sessionId}`] });

      // Mark this message as bookmarked
      setBookmarkedMessages(prev => new Set(prev).add(data.chatMessageId));

      // Switch to Questions in the right panel if available
      if (onRightPanelToggle) {
        onRightPanelToggle('questions');
      }

      toast({
        title: t('chat.addedToQuestionsTitle'),
        description: t('chat.addedToQuestionsDescription'),
      });
    },
    onError: (error) => {
      console.error('[Bookmark] Error saving question:', error);
      toast({
        title: t('chat.bookmarkErrorTitle'),
        description: error instanceof Error ? error.message : t('chat.bookmarkErrorDescription'),
        variant: "destructive",
      });
    },
  });

  const handleBookmark = (chatMessageId: number, question: string, aiAnswer: string) => {
    bookmarkMutation.mutate({
      question,
      aiContext: aiAnswer,
      chatMessageId,
    });
  };

  // Convert messages to display format
  // Use only database messages (they're saved immediately after streaming)
  const displayMessages: Array<{ id: string; content: string; isUser: boolean; timestamp: string; chatMessageId?: number; question?: string }> = [];

  messages.forEach((msg: { id: number; message: string; response: string | null; createdAt: string }) => {
    // Add user message
    displayMessages.push({
      id: `${msg.id}-user`,
      content: msg.message,
      isUser: true,
      timestamp: msg.createdAt,
    });

    // Add AI response
    if (msg.response) {
      displayMessages.push({
        id: `${msg.id}-ai`,
        content: msg.response,
        isUser: false,
        timestamp: msg.createdAt,
        chatMessageId: msg.id,
        question: msg.message,
      });
    }
  });

  return (
    <div
      className={`h-full flex flex-col ${className}`}
      style={{ display: isVisible ? 'flex' : 'none' }}
      data-chat-interface
    >
      {displayMessages.length === 0 && !isStreaming && !streamingMessage && !pendingUserMessage ? (
        /* Empty State - Centered like Gemini/Claude */
        <div className="flex-1 flex flex-col items-center justify-center px-3 sm:px-4 pt-12 sm:pt-16 pb-24 sm:pb-32 relative">
          {/* Expand button in top-right corner for empty state - Hidden on mobile */}
          {onToggleExpand && (
            <div className="absolute top-4 right-4 hidden md:block">
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleExpand}
                title={isExpanded ? t('chat.openSidebar') : t('chat.expandToFullScreen')}
                data-testid="button-toggle-expand-chat-empty"
              >
                {isExpanded ? <PanelRightOpen className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            </div>
          )}
          <div className="w-full max-w-3xl">
            {showStartupCard ? (
              <h2 className="text-xl sm:text-2xl font-medium text-gray-800 mb-4 text-center">
                {t('chat.startupHeading', { name: firstName })}
              </h2>
            ) : (
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-medium text-gray-800 mb-2">
                  {t('chat.greeting')}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600">
                  {t('chat.emptyStateSubtitle', { name: firstName })}
                </p>
              </div>
            )}

            {/* Input in Center */}
            <div className="mb-4 sm:mb-6">
              <div className="flex space-x-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t('chat.inputPlaceholder', { name: firstName })}
                  className="flex-1 h-11 sm:h-12 text-sm sm:text-base"
                  disabled={isStreaming}
                  data-testid="input-chat-message"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isStreaming}
                  className="bg-primary hover:bg-primary-dark h-11 sm:h-12 px-4 sm:px-6"
                  aria-label={t('chat.send')}
                  title={t('chat.send')}
                  data-testid="button-send-message"
                >
                  <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              </div>
            </div>

            {/* Patient Summary card (startup generation) - Hidden once user interacts with chat */}
            {showStartupCard && (
              <div className="mt-6 sm:mt-8">
                {renderStartupCard()}
              </div>
            )}

            {/* Suggested Questions - Hidden once user interacts with chat */}
            {!showStartupCard && suggestedQuestions.length > 0 && !hasInteracted && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-medium">{t('chat.suggestedQuestions')}</p>
                <div className="grid gap-2">
                  {suggestedQuestions.slice(0, 3).map((question, index) => (
                    <button
                      key={index}
                      className="text-left p-2.5 sm:p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-xs sm:text-sm text-gray-700"
                      onClick={() => askStartupTopic(question)}
                      data-testid={`button-suggested-question-${index}`}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-gray-400 text-center mt-3">
              {t('chat.disclaimer')}
            </p>
          </div>
        </div>
      ) : (
        /* Chat View - Full Screen Like Claude */
        <>
          {/* Messages */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto relative">
            {/* Expand button in top-right corner for chat with messages - Hidden on mobile */}
            {onToggleExpand && (
              <div className="sticky top-0 z-10 justify-end p-2 bg-gradient-to-b from-white via-white to-transparent pointer-events-none hidden md:flex">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onToggleExpand}
                  title={isExpanded ? t('chat.openSidebar') : t('chat.expandToFullScreen')}
                  data-testid="button-toggle-expand-chat-messages"
                  className="pointer-events-auto"
                >
                  {isExpanded ? <PanelRightOpen className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              </div>
            )}
            <div className="max-w-3xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">

              {displayMessages.map((msg, index) => (
                <div key={msg.id} className="w-full" data-message data-index={index}>
                  {msg.isUser ? (
                    /* User Message - Right Aligned */
                    <div className="flex justify-end mb-3 sm:mb-4">
                      <div className="bg-gray-100 rounded-2xl px-3 sm:px-4 py-2 sm:py-2.5 max-w-[85%] sm:max-w-[80%]">
                        <p className="text-xs sm:text-sm text-gray-900 whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ) : (
                    /* AI Message - Left Aligned with Icon */
                    <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
                      <div className="w-6 h-6 sm:w-7 sm:h-7 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                        <Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={AI_MESSAGE_PROSE_CLASSES}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.content}
                          </ReactMarkdown>
                        </div>
                        {msg.chatMessageId && msg.question && (
                          <button
                            onClick={() => handleBookmark(msg.chatMessageId!, msg.question!, msg.content)}
                            disabled={bookmarkMutation.isPending || bookmarkedMessages.has(msg.chatMessageId)}
                            className={`mt-3 flex items-center gap-2 px-3 py-2 text-xs font-medium border rounded-lg transition-all group shadow-sm hover:shadow disabled:cursor-not-allowed ${
                              bookmarkedMessages.has(msg.chatMessageId)
                                ? 'text-white bg-primary border-primary'
                                : 'text-primary bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-primary disabled:opacity-50'
                            }`}
                            data-testid={`button-bookmark-${msg.chatMessageId}`}
                          >
                            <Bookmark className={`h-4 w-4 ${bookmarkedMessages.has(msg.chatMessageId) ? 'fill-current' : 'group-hover:fill-current'}`} />
                            <span>{bookmarkedMessages.has(msg.chatMessageId) ? t('chat.addedToCareQuestions') : t('chat.addToCareQuestions')}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Pending user message - appears immediately when sent */}
              {pendingUserMessage && (
                <div className="w-full" data-message>
                  <div className="flex justify-end mb-3 sm:mb-4">
                    <div className="bg-gray-100 rounded-2xl px-3 sm:px-4 py-2 sm:py-2.5 max-w-[85%] sm:max-w-[80%]">
                      <p className="text-xs sm:text-sm text-gray-900 whitespace-pre-wrap">{pendingUserMessage}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Streaming AI response - show even after streaming completes */}
              {streamingMessage && (
                <div ref={loadingIndicatorRef} className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4" data-message>
                  <div className="w-6 h-6 sm:w-7 sm:h-7 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                    <Bot className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={AI_MESSAGE_PROSE_CLASSES}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {streamingMessage}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading indicator when waiting for first chunk */}
              {isStreaming && !streamingMessage && (
                <div ref={loadingIndicatorRef} className="flex items-start gap-3 mb-4">
                  <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </div>
                </div>
              )}

              {/* Inline error message with retry button */}
              {chatError && (
                <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4" data-message>
                  <div className="w-6 h-6 sm:w-7 sm:h-7 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                    <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                      <p className="text-xs sm:text-sm text-red-700">
                        {t('chat.errorPrefix', { message: chatError.message })}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const failedQuestion = chatError.failedQuestion;
                          const failedDisplayText = chatError.failedDisplayText;
                          setChatError(null);
                          setPendingUserMessage("");
                          sendStreamingMessage(failedQuestion, failedDisplayText);
                        }}
                        className="mt-2 text-xs border-red-300 text-red-700 hover:bg-red-100"
                      >
                        <RotateCcw className="h-3 w-3 mr-1.5" />
                        {t('chat.tryAgain')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
          
          {/* Chat Input - Fixed at Bottom */}
          <div className="border-t border-gray-200">
            <div className="max-w-3xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
              <div className="flex space-x-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t('chat.inputPlaceholder', { name: firstName })}
                  className="flex-1 h-11 sm:h-12 text-sm sm:text-base"
                  disabled={isStreaming}
                  data-testid="input-chat-message"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isStreaming}
                  className="bg-primary hover:bg-primary-dark h-11 sm:h-12 px-4 sm:px-6"
                  aria-label={t('chat.send')}
                  title={t('chat.send')}
                  data-testid="button-send-message"
                >
                  <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              </div>

              {/* Patient Summary card Below Input - Hidden once user interacts with chat */}
              {showStartupCard && (
                <div className="mt-2 sm:mt-3">
                  {renderStartupCard()}
                </div>
              )}

              {/* Suggested Questions Below Input - Hidden once user interacts with chat */}
              {!showStartupCard && suggestedQuestions.length > 0 && !hasInteracted && (
                <div className="mt-2 sm:mt-3 space-y-2">
                  <p className="text-xs text-gray-500 font-medium">{t('chat.suggestedQuestions')}</p>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {suggestedQuestions.slice(0, 3).map((question, index) => (
                      <button
                        key={index}
                        className="text-left px-2.5 sm:px-3 py-1.5 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors text-xs text-gray-700"
                        onClick={() => askStartupTopic(question)}
                        data-testid={`button-suggested-question-${index}`}
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-400 text-center mt-2">
                {t('chat.disclaimer')}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

ChatInterface.displayName = 'ChatInterface';

export default ChatInterface;
